import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Button } from './ui/button';

const ProductModal = ({ product, isOpen, onClose, onAddToCart }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { addToCart } = useCart();

  if (!isOpen || !product) return null;

  const images = product.imagens && product.imagens.length > 0 
    ? product.imagens 
    : [product.url_imagem];

  const handleAddToCart = () => {
    addToCart(product);
    if (onAddToCart) {
      onAddToCart();
    }
    onClose();
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <>
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }}
        onClick={onClose}
        data-testid="product-modal-overlay"
      />
      
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '12px',
          maxWidth: '900px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          zIndex: 1000,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="product-modal"
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            backgroundColor: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 10
          }}
          data-testid="modal-close-button"
        >
          <X size={24} />
        </button>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', padding: '1.5rem' }} className="modal-grid">
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1', overflow: 'hidden', borderRadius: '8px' }}>
              <img
                src={images[currentImageIndex]}
                alt={product.nome}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                data-testid="modal-product-image"
              />
            </div>

            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  style={{
                    position: 'absolute',
                    left: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                  data-testid="modal-prev-image"
                >
                  <ChevronLeft size={20} />
                </button>

                <button
                  onClick={nextImage}
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                  data-testid="modal-next-image"
                >
                  <ChevronRight size={20} />
                </button>

                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  marginTop: '1rem',
                  overflowX: 'auto',
                  paddingBottom: '0.5rem'
                }}>
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      style={{
                        width: '60px',
                        height: '60px',
                        border: index === currentImageIndex ? '2px solid #000' : '2px solid transparent',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        flexShrink: 0,
                        padding: 0
                      }}
                      data-testid={`modal-thumbnail-${index}`}
                    >
                      <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', marginBottom: '0.5rem' }}>
                {product.nome}
              </h2>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>{product.categoria}</p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {product.tags && product.tags.map((tag, idx) => (
                <span
                  key={idx}
                  style={{
                    backgroundColor: '#E8DCC4',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '20px',
                    fontSize: '0.875rem'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <p style={{ fontSize: '2rem', fontFamily: 'Playfair Display, serif', fontWeight: 'bold' }}>
              R$ {product.preco.toFixed(2)}
            </p>

            <div>
              <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem', fontWeight: '600' }}>Descrição</h3>
              <p style={{ color: '#333', lineHeight: '1.6' }}>{product.descricao}</p>
            </div>

            <Button
              onClick={handleAddToCart}
              style={{
                backgroundColor: '#000',
                color: '#fff',
                padding: '1rem',
                fontSize: '1.1rem',
                marginTop: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              data-testid="modal-add-to-cart"
            >
              <ShoppingCart size={20} />
              Adicionar ao Carrinho
            </Button>
          </div>
        </div>

        <style>{`
          @media (min-width: 768px) {
            .modal-grid {
              grid-template-columns: 1fr 1fr !important;
              gap: 2rem !important;
              padding: 2rem !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default ProductModal;